import { useEffect, useRef, useState } from 'react';
import { useTooltip } from '@/hooks/useTooltip';
import { Icon } from '@/components/Icon';

export interface MultiSelectOption {
  id: number;
  name: string;
}

/**
 * Мультиселект-«тег-инпут»: выбранное – чипами, остальное – в выпадающем
 * списке. Общий для кураторов клиента и ролей администратора; тексты
 * (тултип, плейсхолдер, «нет доступных …») задаются пропсами.
 */
export function MultiSelect({
  values,
  options,
  disabled,
  onChange,
  label,
  placeholder = 'Не выбрано',
  emptyText = 'Нет доступных значений',
  selectAll = false,
}: {
  values: number[];
  options: MultiSelectOption[] | undefined;
  disabled: boolean;
  onChange: (ids: number[]) => void;
  /** Текст тултипа на контроле. */
  label: string;
  placeholder?: string;
  emptyText?: string;
  /** Служебный пункт «Выбрать все» над списком — там, где вариантов много. */
  selectAll?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const selected = (options ?? []).filter((o) => values.includes(o.id));
  const available = (options ?? []).filter((o) => !values.includes(o.id));

  const remove = (id: number) => onChange(values.filter((existing) => existing !== id));
  const add = (id: number) => onChange([...values, id]);
  const tooltip = useTooltip<HTMLButtonElement>(label);

  return (
    <div className="multiselect" ref={containerRef}>
      <button
        ref={tooltip.ref}
        type="button"
        className="multiselect__control"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        {...tooltip.handlers}
      >
        {selected.length === 0 ? (
          <span className="multiselect__placeholder">{placeholder}</span>
        ) : (
          <span className="multiselect__chips">
            {selected.map((o) => (
              <span key={o.id} className="multiselect__chip">
                {o.name}
                <span
                  className="multiselect__chip-remove"
                  role="button"
                  aria-label={`Убрать ${o.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(o.id);
                  }}
                >
                  <Icon name="close" size={10} />
                </span>
              </span>
            ))}
          </span>
        )}
        <Icon name="chevronDown" size={12} className="multiselect__arrow" />
        {tooltip.node}
      </button>

      {open && !disabled && (
        <div className="multiselect__dropdown">
          {available.length === 0 ? (
            <div className="multiselect__empty">{emptyText}</div>
          ) : (
            <>
              {selectAll && (
                <button
                  type="button"
                  className="multiselect__option multiselect__option--all"
                  onClick={() => onChange((options ?? []).map((o) => o.id))}
                >
                  Выбрать все
                </button>
              )}
              {available.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="multiselect__option"
                  onClick={() => add(o.id)}
                >
                  {o.name}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
